// src/pages/Dashboard/Admin/Requests/components/RequestDetailsDrawer.jsx

import React from "react";
import {
  FaBug,
  FaCalendarAlt,
  FaEdit,
  FaExclamationTriangle,
  FaTasks,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

import {
  formatDateTimeFR,
  getPriorityMeta,
  getRequestCreatedAt,
  getStatusMeta,
  sanitizeHtml,
} from "../utils/requestsDashboardUtils";

function MetaCard({ label, value, icon, badgeClass }) {
  return (
    <div className="rounded-[22px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            {label}
          </p>

          {badgeClass ? (
            <span
              className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${badgeClass}`}
            >
              {value}
            </span>
          ) : (
            <p className="mt-2 text-sm font-bold text-slate-900 dark:text-white">
              {value}
            </p>
          )}
        </div>

        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:text-indigo-300 dark:ring-slate-700">
          {icon}
        </div>
      </div>
    </div>
  );
}

export default function RequestDetailsDrawer({
  isOpen,
  request,
  deletingId,
  onClose,
  onEdit,
  onDelete,
}) {
  if (!isOpen || !request) return null;

  const priority = getPriorityMeta(request.priority);
  const status = getStatusMeta(request.status);

  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Détail requête
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaBug className="text-indigo-600 dark:text-indigo-300" />
              {request.title || "Requête sans titre"}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Description complète de la demande transmise au support
              MonAppliClub.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          <section className="grid gap-4 sm:grid-cols-3">
            <MetaCard
              label="Priorité"
              value={priority.label}
              icon={<FaExclamationTriangle />}
              badgeClass={priority.badge}
            />

            <MetaCard
              label="Statut"
              value={status.label}
              icon={<FaTasks />}
              badgeClass={status.badge}
            />

            <MetaCard
              label="Créée le"
              value={formatDateTimeFR(getRequestCreatedAt(request))}
              icon={<FaCalendarAlt />}
            />
          </section>

          <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
            <div className="border-b border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
              <p className="text-sm font-bold text-slate-900 dark:text-white">
                Description
              </p>
            </div>

            <div className="p-5">
              <div
                className="prose prose-sm max-w-none text-slate-700 dark:prose-invert dark:text-slate-200"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    request.description || "<p>Aucune description.</p>"
                  ),
                }}
              />
            </div>
          </section>

          <section className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              Captures d’écran
            </p>

            <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
              Le système actuel n’a pas encore de pièces jointes dédiées. Les
              images éventuellement intégrées dans la description apparaîtront
              dans le bloc ci-dessus. Pour un vrai suivi de captures, il faudra
              ajouter un endpoint d’upload et une table d’attachments.
            </p>
          </section>
        </div>

        <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Fermer
            </button>

            <button
              type="button"
              onClick={() => onEdit(request)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm font-bold text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300"
            >
              <FaEdit />
              Modifier
            </button>

            <button
              type="button"
              onClick={() => onDelete(request)}
              disabled={deletingId === request.id}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
            >
              <FaTrash />
              Supprimer
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}